import { MagicCreditsInfo } from './types'

/**
 * Per-creator AI credit state, shared by MagicCredits, AiCreditsNotice and
 * checkAndShowAiCreditsModal so the display, the note and the gate can never
 * disagree about whether somebody is blocked.
 *
 * Mirrors getAiCreditsState in gnowbe-frontend
 * (packages/ui/src/components/MagicCreator/components/AiCreditsNotice.tsx).
 * Keep the two in step — a creator seeing different answers on legacy web and
 * the new web app is worse than either answer alone.
 *
 * The behaviour of both is pinned by a shared case table in
 * `apps/frontend/__tests__/AiCreditsState.unit.test.ts` over there. It is not
 * duplicated here because this repo's jest suite does not currently compile or
 * run (`jest-environment-jsdom` is missing and `expect` resolves to chai's
 * types — `Toggle.test.tsx` fails the same way). If that gets fixed, copy the
 * `agrees with the server gate` describe block across; until then that table is
 * the only executable guard, so any change here must be checked against it.
 */

/** Below this share of their allocation, a creator gets a warning rather than a block. */
export const AI_CREDITS_LOW_BALANCE_RATIO = 0.2

export type AiCreditsState = 'ok' | 'low' | 'insufficient' | 'notAssigned' | 'workspaceExhausted'

/**
 * Returns `'ok'` for every workspace that does not meter AI credits per creator,
 * so callers can use it unconditionally — an exhausted pool there is the legacy
 * upsell's business, not this one's.
 *
 * **Mirrors `checkAiCreditsGate` in api-2 (`ai_credits_creator.ts`), including the
 * order of its checks.** The pool is tested before the creator's own balance
 * there, so it has to be here too: `creator.availableCredits` is the creator's
 * unclamped allocation, and judging by it alone showed a healthy counter right up
 * until Create failed with a pool error.
 */
export const getAiCreditsState = (credits?: MagicCreditsInfo | null): AiCreditsState => {
  const creator = credits?.creator
  if (!credits?.creatorCreditsEnforced || !creator) return 'ok'

  // totalCredits is 0 before the first estimate lands; that is not a shortfall.
  const needed = credits.totalCredits || 0

  // Pool first, exactly as the server does. Assigning more cannot fix this one,
  // so it must not be reported as a creator shortfall.
  const pool = credits.workspaceAvailableCredits
  if (needed > 0 && typeof pool === 'number' && needed > pool) return 'workspaceExhausted'

  if (!creator.isAssigned) return 'notAssigned'

  const available = creator.availableCredits
  if (available <= 0 || (needed > 0 && needed > available)) return 'insufficient'

  if (creator.assignedCredits > 0 && available <= creator.assignedCredits * AI_CREDITS_LOW_BALANCE_RATIO) {
    return 'low'
  }
  return 'ok'
}
